import { format, isValid, parseISO } from 'date-fns';
import type { Claim, Policy } from '@/types';
import type { ClaimCase, ClaimOutcome, ClaimStage } from '@/types/claim';
import { whatsappUrl } from '@/lib/support';
import { imageSrc, planName } from './productCases';

/** "2026-10-30T11:20:00Z" → "30 Oct 2026, 11:20 AM". */
export const formatDateTime = (value?: string) => {
  const d = value ? parseISO(value) : null;
  return d && isValid(d) ? format(d, 'dd MMM yyyy, h:mm a') : '';
};

export const claimLabel = (id: string) =>
  id.toUpperCase().startsWith('CLM') ? id.toUpperCase() : `CLM-${id.replace(/-/g, '').slice(0, 6).toUpperCase()}`;

const ISSUE_LABELS: [RegExp, string][] = [
  [/ship|transit|lost|deliver/i, 'Shipping issue'],
  [/accident|damage|break|crack/i, 'Accidental damage'],
  [/manufactur|defect|warrant/i, 'Manufacturing defect'],
];

const issueLabel = (type?: string) => {
  if (!type) return undefined;
  const match = ISSUE_LABELS.find(([pattern]) => pattern.test(type));
  return match ? match[1] : type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
};

const PLAN_TYPE = (type?: string) => {
  const t = String(type ?? '').toLowerCase();
  if (t.includes('ship')) return 'Shipping Protection';
  if (t.includes('extend') || t.includes('manufactur') || t.includes('warrant')) return 'Extended Warranty';
  return 'Accidental Damage';
};

function stageOf(claim: Claim): { stage: ClaimStage; outcome?: ClaimOutcome } {
  const status = String(claim.status ?? '').trim().toLowerCase();
  if (status === 'approved' || status === 'resolved' || status === 'closed') return { stage: 'closed', outcome: 'approved' };
  if (status === 'rejected' || status === 'declined') return { stage: 'closed', outcome: 'rejected' };
  if (status === 'cancelled' || status === 'canceled') return { stage: 'closed', outcome: 'cancelled' };
  if (claim.mode === 'resume') return { stage: 'draft' };
  if (status === 'under_review' || status === 'in_review' || status === 'review') return { stage: 'review' };
  return { stage: 'submitted' };
}

const eventDate = (claim: Claim, events: string[]) => claim.timeline?.find((t) => events.includes(t.event))?.date;

/** Claims joined with their policy for plan and merchant details, newest first. */
export function buildClaimCases(claims: Claim[], policies: Policy[]): ClaimCase[] {
  const byId = new Map(policies.map((p) => [String(p.id), p]));
  return claims
    .map((claim): ClaimCase => {
      const policy = byId.get(String(claim.policyId)) ?? policies.find((p) => claim.policyNumber && p.name === claim.policyNumber);
      const { stage, outcome } = stageOf(claim);
      const start = policy?.timeline?.find((t) => t.event === 'Coverage Started')?.date;
      return {
        id: String(claim.id),
        label: claimLabel(String(claim.id)),
        productTitle: claim.productName || policy?.productName || 'Product',
        imageUrl: imageSrc(claim.imageUrl) ?? imageSrc(policy?.imageUrl),
        merchant: policy?.merchantName,
        planName: policy ? planName(policy.type, start, policy.expiryDate) : undefined,
        planType: policy ? PLAN_TYPE(policy.type) : undefined,
        policyNumber: policy?.name ?? claim.policyNumber,
        issue: issueLabel(claim.claimType),
        description: claim.description && claim.description !== 'No description provided' ? claim.description : undefined,
        photos: (claim.evidence ?? []).filter((url) => typeof url === 'string' && url.length > 0),
        stage,
        outcome,
        filedAt: claim.filedAt,
        reviewedAt: eventDate(claim, ['Reviewed']),
        closedAt: eventDate(claim, ['Claim Approved', 'Claim Rejected']),
      };
    })
    .sort((a, b) => new Date(b.filedAt ?? 0).getTime() - new Date(a.filedAt ?? 0).getTime());
}

// ---------- presentation ----------

/** Step number (1–4) on the Submitted → Review → Resolution → Closed bar. */
export const claimStep = (c: ClaimCase) => ({ draft: 0, submitted: 1, review: 2, closed: 4 })[c.stage];

export const CLAIM_STEP_NAMES = ['Submitted', 'Review', 'Resolution', 'Closed'];

export const claimStatus = (c: ClaimCase): { tone: 'info' | 'warning' | 'success' | 'danger' | 'neutral'; label: string } => {
  if (c.stage === 'draft') return { tone: 'warning', label: 'Incomplete' };
  if (c.stage === 'submitted') return { tone: 'info', label: 'Submitted' };
  if (c.stage === 'review') return { tone: 'info', label: 'In review' };
  if (c.outcome === 'approved') return { tone: 'success', label: 'Approved' };
  if (c.outcome === 'rejected') return { tone: 'danger', label: 'Not approved' };
  return { tone: 'neutral', label: 'Cancelled' };
};

/** Pre-filled WhatsApp message quoting the claim ID. */
export const claimWhatsAppUrl = (c: ClaimCase) => whatsappUrl(`Hi, I have a question about my claim ${c.label}`);
